import { QueryClientProvider } from "@tanstack/react-query";
import { createBrowserHistory } from "history";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Provider } from "react-redux";
import { Link, NavLink, Router } from "react-router-dom";
import { CompatRouter } from "react-router-dom-v5-compat";
import { IntercomProvider } from "react-use-intercom";

import { makeSeneKitProvider } from "@seneca/senekit";

import { queryClient } from "services";
import PushNotificationsProvider from "services/firebase/messaging/PushNotificationsProvider";
import registerClients from "services/registerClients";

import {
  initializeGoogleTagManager,
  pushGlobalValuesToDataLayer,
  pushUserPropertiesToDataLayer
} from "seneca-common/features/analytics/gtm";
import useEffectOnMount from "seneca-common/utils/hooks/useEffectOnMount";

import AppBackground from "features/app-background/AppBackground";
import makeAppErrorHandler from "features/app/components/makeAppErrorHandler";
import ResumeSession from "features/app/components/ResumeSession";
import UpdateUserLastVisit from "features/app/components/UpdateUserLastVisit";
import useShowPWAUpdateToastEffect from "features/app/hooks/useShowPWAUpdateToastEffect";
import { CroppedImageBackgroundDefs } from "features/common/components/CroppedImageBackground";
import GeneralAppError from "features/common/components/GeneralAppError";
import ModalBodyScrollLocker from "features/common/containers/ModalBodyScrollLocker";
import { ModalNodeProvider } from "features/common/modals/components/ModalNodeContext";
import { LastLocationProvider } from "features/common/routing/with-last-location";
import AppThemeProvider from "features/common/themes";
import GlobalCSS from "features/common/themes/GlobalCSS";
import ToastProvider from "features/common/toasts/ToastProvider";
import { TopBarProvider } from "features/common/top-bars";
import {
  getMobileDeviceType,
  isMobile
} from "features/common/utils/functions/mobile";
import DevToolsProvider from "features/dev-tools/Provider";
import { LocalisationProvider } from "features/localisation";
import LocalStorageBlockedProvider from "features/localStorageBlocked";
import OfflineScreen from "features/offline-screen";
import PWAMobileProvider from "features/pwa/mobile/PWAMobileProvider";

import AppInstanceProvider from "./AppInstanceProvider";
import getConfig from "./config";
import populateCypressGlobals from "./cypressGlobals";
import { FlagsProvider } from "./features/feature-flags";
import Routes from "./Routes";
import getStore from "./store";

const SeneKitProvider = makeSeneKitProvider({ createPortal, Link, NavLink });

function App() {
  const [history] = useState(() => createBrowserHistory());
  const store = getStore(history);
  const config = getConfig();
  const INTERCOM_APP_ID = config.intercomId;

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized) {
      registerClients(config, store);
      initializeGoogleTagManager();
      populateCypressGlobals();
      setInitialized(true);
    }
  }, [initialized, store, config]);

  useEffectOnMount(() => {
    pushGlobalValuesToDataLayer({
      appVersion: process.env.REACT_APP_VERSION,
      isMobile: isMobile(),
      mobileDeviceType: getMobileDeviceType()
    });
    pushUserPropertiesToDataLayer({
      appVersion: process.env.REACT_APP_VERSION
    });
  });

  return (
    <Provider store={store}>
      <Router history={history}>
        <CompatRouter>
          {/* @ts-ignore fix-in-senekit */}
          <SeneKitProvider value={{ createPortal, Link, NavLink }}>
            <IntercomProvider
              appId={INTERCOM_APP_ID}
              autoBoot
              autoBootProps={{ hideDefaultLauncher: true }}
            >
              <QueryClientProvider client={queryClient}>
                <FlagsProvider>
                  <ResumeSession initialized={initialized}>
                    <AppThemeProvider>
                      <GlobalCSS />
                      <PushNotificationsProvider>
                        <DevToolsProvider>
                          <AppInstanceProvider>
                            <UpdateUserLastVisit initialized={initialized}>
                              {/* @ts-ignore */}
                              <ModalBodyScrollLocker>
                                <ModalNodeProvider>
                                  <LocalStorageBlockedProvider>
                                    <PWAVersionUpdateHandler>
                                      <PWAMobileProvider>
                                        <LocalisationProvider>
                                          <LastLocationProvider>
                                            <TopBarProvider>
                                              <AppBackground>
                                                <Routes />
                                              </AppBackground>
                                            </TopBarProvider>
                                          </LastLocationProvider>
                                          <ToastProvider />
                                        </LocalisationProvider>
                                      </PWAMobileProvider>
                                    </PWAVersionUpdateHandler>
                                  </LocalStorageBlockedProvider>
                                </ModalNodeProvider>
                              </ModalBodyScrollLocker>
                            </UpdateUserLastVisit>
                          </AppInstanceProvider>
                          <CroppedImageBackgroundDefs />
                        </DevToolsProvider>
                      </PushNotificationsProvider>
                    </AppThemeProvider>
                  </ResumeSession>
                </FlagsProvider>
              </QueryClientProvider>
            </IntercomProvider>
          </SeneKitProvider>
        </CompatRouter>
      </Router>
    </Provider>
  );
}

function PWAVersionUpdateHandler({ children }: { children: any }) {
  // Needs to be within the Redux Provider as we look in the store
  useShowPWAUpdateToastEffect();
  return children;
}

export default makeAppErrorHandler(App, {
  NoConfigComp: OfflineScreen,
  UncaughtErrorComp: GeneralAppError
});
